import React, { useState } from "react";
import {
  Modal,
  Flex,
  Typography,
  Input,
  Select,
  Checkbox,
  Card,
  Button,
  Divider,
  Tag,
  message,
} from "antd";
import {
  WsProduct,
  useBulkUpdateProductsMutation,
} from "../../../api/webshop/wsProductsApi";

const { Title, Text } = Typography;

interface BulkOperationsProps {
  open: boolean;
  onClose: () => void;
  products: WsProduct[];
}

export default function BulkOperations({
  open,
  onClose,
  products,
}: BulkOperationsProps) {
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [bulkUpdateProducts, { isLoading: isUpdating }] =
    useBulkUpdateProductsMutation();
  const [tags, setTags] = useState<string[]>([]);

  const [priceDiscountValue, setPriceDiscountValue] = useState("");
  const [priceDiscountUnit, setPriceDiscountUnit] =
    useState<"PERCENT">("PERCENT");

  const [dataBonusLabel, setDataBonusLabel] = useState("");
  const [dataBonusValue, setDataBonusValue] = useState("");
  const [promotionalTagValue, setPromotionalTagValue] = useState("");

  const [removeProductTags, setRemoveProductTags] = useState(false);
  const [removePromotionalOffers, setRemovePromotionalOffers] = useState(false);
  const [removePromotionalTag, setRemovePromotionalTag] = useState(false);
  const [removeBulkDiscount, setRemoveBulkDiscount] = useState(false);
  const [removeDataBonus, setRemoveDataBonus] = useState(false);

  const handleApply = async () => {
    if (selectedProductIds.length === 0) {
      message.warning("Please select at least one product to update.");
      return;
    }

    const finalData: Record<string, any> = {};

    selectedProductIds.forEach((id) => {
      let productPromotions: any = {};

      if (removePromotionalOffers) {
        productPromotions = "DELETE";
      } else {
        if (removePromotionalTag) {
          productPromotions.tag = "DELETE";
        } else if (promotionalTagValue) {
          productPromotions.tag = promotionalTagValue;
        }

        if (removeBulkDiscount) {
          productPromotions.priceTag = "DELETE";
        } else if (priceDiscountValue) {
          if (!productPromotions.tag) productPromotions.tag = "PRICE DISCOUNT";
          productPromotions.priceTag = {
            value: priceDiscountValue,
            unit: priceDiscountUnit,
          };
        }

        if (removeDataBonus) {
          productPromotions.dataTag = "DELETE";
        } else if (dataBonusValue && dataBonusLabel) {
          if (!productPromotions.tag) productPromotions.tag = "BONUS DATA";
          productPromotions.dataTag = {
            label: dataBonusLabel,
            value: dataBonusValue,
          };
        }
      }

      const idData: any = {};

      if (removeProductTags) {
        idData.productTags = "DELETE";
      } else if (tags.length > 0) {
        idData.productTags = tags.join(", ");
      }

      if (productPromotions === "DELETE") {
        idData.productPromotions = "DELETE";
      } else if (Object.keys(productPromotions).length > 0) {
        idData.productPromotions = productPromotions;
      }

      if (Object.keys(idData).length > 0) {
        finalData[id] = idData;
      } else {
        finalData[id] = { productTags: undefined };
      }
    });

    try {
      await bulkUpdateProducts(finalData).unwrap();
      message.success(
        `Successfully updated ${selectedProductIds.length} products`,
      );
      onClose();
    } catch (err: any) {
      console.error("Bulk update failed:", err);
      message.error(err?.data?.message || "Failed to perform bulk update");
    }
  };

  const handleSelectAll = (e: any) => {
    if (e.target.checked) {
      setSelectedProductIds(products.map((p) => p.productId));
    } else {
      setSelectedProductIds([]);
    }
  };

  return (
    <Modal
      title="Bulk Operations"
      open={open}
      onCancel={onClose}
      width={1000}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={isUpdating}>
          Cancel
        </Button>,
        <Button
          key="apply"
          type="primary"
          onClick={handleApply}
          loading={isUpdating}
        >
          Save
        </Button>,
      ]}
    >
      <Flex gap={24} style={{ minHeight: 400, marginTop: 16 }}>
        {/* Left Section: Bulk Operations */}
        <Flex vertical gap={16} style={{ flex: 1 }}>
          <Title level={5} style={{ marginBottom: 0 }}>
            Operations
          </Title>

          <Card
            size="small"
            style={{ backgroundColor: "#fafafa", borderColor: "#f0f0f0" }}
          >
            <Flex vertical gap={8}>
              <Flex justify="space-between" align="center">
                <Text strong>Product Tags</Text>
                <Checkbox
                  checked={removeProductTags}
                  onChange={(e) => setRemoveProductTags(e.target.checked)}
                  style={{
                    border: "1px solid #d9d9d9",
                    padding: "2px 8px",
                    borderRadius: 4,
                    background: removeProductTags ? "#fff1f0" : "#fff",
                  }}
                >
                  Remove Tags
                </Checkbox>
              </Flex>
              <Select
                mode="tags"
                style={{ width: "100%" }}
                placeholder="Type tags and press comma or enter"
                tokenSeparators={[","]}
                value={tags}
                onChange={setTags}
                disabled={removeProductTags}
              />
            </Flex>
          </Card>

          <Card
            size="small"
            style={{ backgroundColor: "#fafafa", borderColor: "#f0f0f0" }}
          >
            <Flex vertical gap={16}>
              <Flex justify="space-between" align="center">
                <Text strong style={{ fontSize: 16 }}>
                  Promotional Offers
                </Text>
                <Checkbox
                  checked={removePromotionalOffers}
                  onChange={(e) => setRemovePromotionalOffers(e.target.checked)}
                  style={{
                    border: "1px solid #d9d9d9",
                    padding: "2px 8px",
                    borderRadius: 4,
                    background: removePromotionalOffers ? "#fff1f0" : "#fff",
                  }}
                >
                  Remove All Offers
                </Checkbox>
              </Flex>

              <Divider style={{ margin: 0 }} />

              <Flex vertical gap={8}>
                <Flex justify="space-between" align="center">
                  <Text strong>Promotional Tag</Text>
                  <Checkbox
                    checked={removePromotionalTag}
                    onChange={(e) => setRemovePromotionalTag(e.target.checked)}
                    disabled={removePromotionalOffers}
                    style={{
                      border: "1px solid #d9d9d9",
                      padding: "2px 8px",
                      borderRadius: 4,
                      background: removePromotionalTag ? "#fff1f0" : "#fff",
                    }}
                  >
                    Remove Tag
                  </Checkbox>
                </Flex>
                <Input
                  placeholder="e.g. BEST SELLER, RECOMMENDED"
                  value={promotionalTagValue}
                  onChange={(e) => setPromotionalTagValue(e.target.value)}
                  disabled={removePromotionalTag || removePromotionalOffers}
                />
              </Flex>

              <Divider style={{ margin: 0 }} />

              <Flex vertical gap={8}>
                <Flex justify="space-between" align="center">
                  <Text strong>Bulk Discount</Text>
                  <Checkbox
                    checked={removeBulkDiscount}
                    onChange={(e) => setRemoveBulkDiscount(e.target.checked)}
                    disabled={removePromotionalOffers}
                    style={{
                      border: "1px solid #d9d9d9",
                      padding: "2px 8px",
                      borderRadius: 4,
                      background: removeBulkDiscount ? "#fff1f0" : "#fff",
                    }}
                  >
                    Remove Discounts
                  </Checkbox>
                </Flex>
                <Flex gap={8}>
                  <Input
                    type="number"
                    placeholder="Discount value"
                    value={priceDiscountValue}
                    onChange={(e) => setPriceDiscountValue(e.target.value)}
                    style={{ flex: 1 }}
                    disabled={removeBulkDiscount || removePromotionalOffers}
                  />
                  <Select
                    value={priceDiscountUnit}
                    onChange={(val) => setPriceDiscountUnit(val as "PERCENT")}
                    options={[{ label: "%", value: "PERCENT" }]}
                    style={{ width: 120 }}
                    disabled={removeBulkDiscount || removePromotionalOffers}
                  />
                </Flex>
              </Flex>

              <Divider style={{ margin: 0 }} />

              <Flex vertical gap={8}>
                <Flex justify="space-between" align="center">
                  <Text strong>Data Bonus</Text>
                  <Checkbox
                    checked={removeDataBonus}
                    onChange={(e) => setRemoveDataBonus(e.target.checked)}
                    disabled={removePromotionalOffers}
                    style={{
                      border: "1px solid #d9d9d9",
                      padding: "2px 8px",
                      borderRadius: 4,
                      background: removeDataBonus ? "#fff1f0" : "#fff",
                    }}
                  >
                    Remove Bonus
                  </Checkbox>
                </Flex>
                <Flex gap={8}>
                  <Input
                    placeholder="Label (e.g. Free 3GB Data)"
                    value={dataBonusLabel}
                    onChange={(e) => setDataBonusLabel(e.target.value)}
                    style={{ flex: 1 }}
                    disabled={removeDataBonus || removePromotionalOffers}
                  />
                  <Input
                    type="number"
                    placeholder="Value in GB"
                    value={dataBonusValue}
                    onChange={(e) => setDataBonusValue(e.target.value)}
                    style={{ width: 120 }}
                    disabled={removeDataBonus || removePromotionalOffers}
                  />
                </Flex>
              </Flex>
            </Flex>
          </Card>
        </Flex>

        <Divider type="vertical" style={{ height: "auto" }} />

        {/* Right Section: Products List */}
        <Flex vertical gap={16} style={{ flex: 1 }}>
          <Flex justify="space-between" align="center">
            <Title level={5} style={{ margin: 0 }}>
              Select Products
            </Title>
            <Checkbox
              onChange={handleSelectAll}
              checked={
                selectedProductIds.length === products.length &&
                products.length > 0
              }
              indeterminate={
                selectedProductIds.length > 0 &&
                selectedProductIds.length < products.length
              }
            >
              Select All
            </Checkbox>
          </Flex>

          <div style={{ maxHeight: 500, overflowY: "auto", paddingRight: 8 }}>
            <Checkbox.Group
              style={{ width: "100%" }}
              value={selectedProductIds}
              onChange={(checkedValues) =>
                setSelectedProductIds(checkedValues as string[])
              }
            >
              <Flex vertical gap={12}>
                {products.map((product) => {
                  const promotions = product.productPromotions;
                  return (
                    <Card
                      size="small"
                      key={product.productId}
                      style={{ width: "100%" }}
                    >
                      <Flex align="flex-start" gap={12}>
                        <Checkbox
                          value={product.productId}
                          style={{ marginTop: 4 }}
                        />
                        <Flex
                          vertical
                          gap={4}
                          style={{ flex: 1, overflow: "hidden" }}
                        >
                          <Text strong ellipsis>
                            {product.title ||
                              product.productTitle ||
                              product.productId}
                          </Text>

                          {product.productTags && (
                            <Flex gap={4} wrap="wrap">
                              Product Tags:{" "}
                              {product.productTags
                                .split(",")
                                .map((tag, idx) => (
                                  <Tag key={idx} color="blue">
                                    {tag.trim()}
                                  </Tag>
                                ))}
                            </Flex>
                          )}

                          {promotions && (
                            <Flex gap={4} wrap="wrap" style={{ marginTop: 4 }}>
                              {promotions.tag && (
                                <Tag color="green">
                                  Promotional Tag: {promotions?.tag}
                                </Tag>
                              )}
                              {promotions.priceTag && (
                                <Tag color="orange">
                                  Discount: {promotions.priceTag.value}
                                  {promotions.priceTag.unit === "PERCENT"
                                    ? "%"
                                    : ""}
                                </Tag>
                              )}
                              {promotions.dataTag && (
                                <Tag color="orange">
                                  Bonus: {promotions.dataTag.value}GB (
                                  {promotions.dataTag.label})
                                </Tag>
                              )}
                            </Flex>
                          )}
                        </Flex>
                      </Flex>
                    </Card>
                  );
                })}
              </Flex>
            </Checkbox.Group>
          </div>
        </Flex>
      </Flex>
    </Modal>
  );
}
